import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

// @mui material components
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// API
import { createPaypalOrder, capturePaypalOrder, logPaypalClientEvent } from "api";

// Tracking
import { trackMetaEvent } from "utils/metaEvents";

function getPaymentErrorMessage(error) {
  const issue = error?.details?.issue;

  if (issue === "ORDER_NOT_APPROVED") {
    return "Payment approval was not completed. Please reopen PayPal and finish the payment step before trying again.";
  }

  if (issue === "PAYMENT_DENIED") {
    return "PayPal could not approve this payment. Please try another card or PayPal account.";
  }

  return error?.message || "Payment failed. Please try again.";
}

function serializePaypalError(error) {
  if (!error) {
    return null;
  }

  return {
    name: error.name || null,
    message: error.message || null,
    status: error.status || null,
    ...(error.details ? { details: error.details } : {}),
    ...(typeof error.stack === "string"
      ? { stack: error.stack.split("\n").slice(0, 3).join("\n") }
      : {}),
  };
}

function PaymentSection({ bookingId, depositAmount, isTestPayment }) {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [checkoutLoadingMessage, setCheckoutLoadingMessage] = useState(null);

  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const checkoutLoadingTimeoutRef = useRef(null);
  const fundingSourceRef = useRef(null);

  const clearCheckoutLoadingMessage = () => {
    if (checkoutLoadingTimeoutRef.current) {
      window.clearTimeout(checkoutLoadingTimeoutRef.current);
      checkoutLoadingTimeoutRef.current = null;
    }
    setCheckoutLoadingMessage(null);
  };

  const showCheckoutLoadingMessage = (message) => {
    clearCheckoutLoadingMessage();
    setCheckoutLoadingMessage(message);
    checkoutLoadingTimeoutRef.current = window.setTimeout(() => {
      checkoutLoadingTimeoutRef.current = null;
      setCheckoutLoadingMessage(null);
    }, 6000);
  };

  useEffect(
    () => () => {
      if (checkoutLoadingTimeoutRef.current) {
        window.clearTimeout(checkoutLoadingTimeoutRef.current);
      }
    },
    []
  );

  const recordPayPalEvent = (paymentEvent) => {
    void logPaypalClientEvent(bookingId, {
      ...paymentEvent,
      details: {
        ...(paymentEvent.details || {}),
        userAgent: window.navigator.userAgent,
      },
    }).catch((error) => {
      console.error("Failed to log PayPal client event:", error);
    });
  };

  const handleSuccess = () => {
    clearCheckoutLoadingMessage();
    fundingSourceRef.current = null;
    setPaymentError(null);
    setConfirmDialog(true);
    if (!isTestPayment && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "AW-18080249618/g-ZeCPHo7JkcEJLuqq1D",
        value: depositAmount,
        currency: "USD",
        transaction_id: bookingId,
      });
    }
    if (!isTestPayment) {
      trackMetaEvent("Purchase", { currency: "USD", value: depositAmount });
    }
  };

  const handleError = (message) => {
    clearCheckoutLoadingMessage();
    setPaymentError(message);
  };

  return (
    <MKBox component="section" py={4}>
      <Container maxWidth="sm">
        <MKTypography variant="h5" mb={3}>
          Payment Method
        </MKTypography>

        {isTestPayment && (
          <MKBox bgColor="warning" borderRadius="lg" p={2} mb={3} sx={{ opacity: 0.95 }}>
            <MKTypography variant="body2" color="white">
              This is a live $1 PayPal test payment created from the owner dashboard. It will not
              block dates or send guest confirmation emails.
            </MKTypography>
          </MKBox>
        )}

        {paymentError && (
          <MKBox bgColor="error" borderRadius="lg" p={2} mb={3} sx={{ opacity: 0.9 }}>
            <MKTypography variant="body2" color="white">
              {paymentError}
            </MKTypography>
          </MKBox>
        )}

        {checkoutLoadingMessage && (
          <MKBox
            display="flex"
            alignItems="center"
            gap={1.5}
            bgColor="grey-100"
            borderRadius="lg"
            p={2}
            mb={3}
            sx={{ border: "1px solid", borderColor: "info.main" }}
          >
            <CircularProgress size={20} sx={{ color: "info.main" }} />
            <MKTypography variant="body2" color="dark" fontWeight="bold">
              {checkoutLoadingMessage}
            </MKTypography>
          </MKBox>
        )}

        {!paypalClientId ? (
          <MKTypography variant="body2" color="text" textAlign="center" py={3}>
            Payment is not configured. Please contact the property owner.
          </MKTypography>
        ) : (
          <PayPalScriptProvider
            options={{
              "client-id": paypalClientId,
              currency: "USD",
              intent: "capture",
              commit: true,
              "enable-funding": "card",
              "disable-funding": "credit,paylater",
            }}
          >
            <MKTypography variant="body2" color="text" mb={2}>
              Pay ${depositAmount.toLocaleString()} deposit with PayPal or debit/credit card.
            </MKTypography>
            <PayPalButtons
              style={{ layout: "vertical", label: "pay", tagline: false }}
              onClick={(data, actions) => {
                fundingSourceRef.current = data?.fundingSource || null;
                showCheckoutLoadingMessage(
                  fundingSourceRef.current === "card"
                    ? "Loading secure card form..."
                    : "Opening secure PayPal checkout..."
                );
                return actions.resolve();
              }}
              createOrder={async () => {
                setPaymentError(null);
                try {
                  const { orderId } = await createPaypalOrder({ bookingId });
                  if (fundingSourceRef.current !== "card") {
                    clearCheckoutLoadingMessage();
                  }
                  return orderId;
                } catch (error) {
                  console.error("PayPal order creation failed:", error);
                  handleError(getPaymentErrorMessage(error));
                  fundingSourceRef.current = null;
                  recordPayPalEvent({
                    stage: "create-order",
                    status: "error",
                    message: getPaymentErrorMessage(error),
                    details: serializePaypalError(error),
                  });
                  throw error;
                }
              }}
              onApprove={async (data) => {
                try {
                  const capture = await capturePaypalOrder(data.orderID);

                  if (capture.status !== "COMPLETED") {
                    throw new Error("PayPal did not confirm the payment. Please try again.");
                  }

                  handleSuccess();
                } catch (error) {
                  console.error("PayPal capture failed:", error);
                  handleError(getPaymentErrorMessage(error));
                  fundingSourceRef.current = null;
                  recordPayPalEvent({
                    stage: "capture-response",
                    status: "error",
                    message: getPaymentErrorMessage(error),
                    issue: error?.details?.issue,
                    debugId: error?.details?.debugId,
                    orderStatus: error?.details?.orderStatus,
                    orderId: data.orderID,
                    details: serializePaypalError(error),
                  });
                }
              }}
              onCancel={(data) => {
                handleError("Payment was cancelled before completion. No charge was made.");
                fundingSourceRef.current = null;
                recordPayPalEvent({
                  stage: "checkout",
                  status: "cancelled",
                  message: "Payment was cancelled before completion. No charge was made.",
                  orderId: data?.orderID,
                });
              }}
              onError={(error) => {
                console.error("PayPal checkout error:", error);
                handleError(getPaymentErrorMessage(error));
                fundingSourceRef.current = null;
                recordPayPalEvent({
                  stage: "paypal-onerror",
                  status: "error",
                  message: getPaymentErrorMessage(error),
                  details: serializePaypalError(error),
                });
              }}
            />
          </PayPalScriptProvider>
        )}
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          <MKBox display="flex" alignItems="center" gap={1}>
            <Icon color="success">check_circle</Icon>
            {isTestPayment ? "Test Payment Received!" : "Deposit Received!"}
          </MKBox>
        </DialogTitle>
        <DialogContent>
          <MKTypography variant="body2" mb={1}>
            {isTestPayment
              ? "Your $1 live PayPal test payment was received. No dates were blocked and no guest emails were sent."
              : "Your deposit has been received! The remaining balance is due upon arrival."}
          </MKTypography>
        </DialogContent>
        <DialogActions>
          <MKButton
            variant="gradient"
            color="info"
            onClick={() => {
              setConfirmDialog(false);
              window.location.href = "/";
            }}
          >
            Back to Home
          </MKButton>
        </DialogActions>
      </Dialog>
    </MKBox>
  );
}

PaymentSection.propTypes = {
  bookingId: PropTypes.string.isRequired,
  depositAmount: PropTypes.number.isRequired,
  isTestPayment: PropTypes.bool,
};

export default PaymentSection;
